import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import type { KpiItem as KpiItemType } from 'types/dashboard';
import { useBooleanState } from 'utils/hooks';
import { SimpleWidgetCard } from 'components/user/Overview/Widgets/InfoWidgets/SimpleWidgetCard';
import { KpiModal } from './KpiModal';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface KpiItemProps {
  kpiItemData: KpiItemType;
  removeItem: () => void;
  isHeliosModule: boolean;
  setKpiData: Dispatch<SetStateAction<KpiItemType>>;
}

export const KpiItem = ({ kpiItemData, removeItem, isHeliosModule, setKpiData }: KpiItemProps) => {
  const [modal, toggleModal] = useBooleanState(Boolean(kpiItemData?.isNew));
  const { t } = useTranslation();

  const name =
    kpiItemData?.kpi && 'link' in kpiItemData.kpi ? kpiItemData.kpi.link?.linkText : kpiItemData?.name || t('core.settings.dashboard.kpiItem');

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(kpiItemData.id),
    disabled: modal,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  const handleCloseModal = useCallback(() => {
    toggleModal();
    if (kpiItemData?.isNew) {
      removeItem();
    }
  }, [kpiItemData.isNew, removeItem, toggleModal]);

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <SimpleWidgetCard name={name} edit onClick={toggleModal} onRemove={removeItem} />
      <KpiModal
        open={modal}
        setKpiData={setKpiData}
        onClose={handleCloseModal}
        onConfirm={toggleModal}
        kpiData={kpiItemData}
        isHeliosModule={isHeliosModule}
      />
    </div>
  );
};
